/**
 * ZipStormBadge — compact one-line storm summary for a ZIP, shown above the
 * lead list. Loads /api/storms/by-zip lazily so it doesn't block the property
 * results from rendering.
 */
import React, { useEffect, useState } from 'react';
import { CloudHail, Wind, Tornado, Cloud, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StormIntelCard } from '@/components/StormIntelCard';
import api from '@/lib/api';

export function ZipStormBadge({ zip, daysBack = 730, radiusMiles = 10, className = '' }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!zip) return undefined;
        let cancelled = false;
        setLoading(true);
        api.get(`/storms/by-zip?zip_code=${encodeURIComponent(zip)}&days_back=${daysBack}&radius_miles=${radiusMiles}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData({ ok: false, summary: { total_events: 0 } }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [zip, daysBack, radiusMiles]);

    if (loading) {
        return (
            <div className={`rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 flex items-center gap-2 ${className}`}>
                <Loader2 className="h-3 w-3 animate-spin" /> Checking storm history for ZIP {zip}…
            </div>
        );
    }

    const s = data?.summary || {};
    const total = s.total_events || 0;
    const monthsBack = Math.round(daysBack / 30);

    if (total === 0) {
        return (
            <div className={`rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2 ${className}`}>
                <Cloud className="h-3.5 w-3.5 text-emerald-600" />
                <span><b>Quiet area</b> — no severe weather reported in ZIP {zip} over the last {monthsBack} months.</span>
            </div>
        );
    }

    return (
        <div className={className}>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="zip-storm-badge">
                <CloudHail className="h-4 w-4 text-amber-700 shrink-0" />
                <span className="text-sm font-medium text-amber-900">
                    {total} severe weather event{total === 1 ? '' : 's'} in {radiusMiles} mi · last {monthsBack} mo
                </span>
                {s.max_hail_in > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-sky-50 border-sky-200 text-sky-700">
                        <CloudHail className="h-2.5 w-2.5 mr-0.5" /> max {s.max_hail_in}&quot; hail
                    </Badge>
                )}
                {s.max_wind_mph > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-300 text-amber-800">
                        <Wind className="h-2.5 w-2.5 mr-0.5" /> max {s.max_wind_mph} mph
                    </Badge>
                )}
                {(s.by_category || []).some((c) => c.category === 'tornado') && (
                    <Badge variant="outline" className="text-[10px] bg-rose-50 border-rose-200 text-rose-700">
                        <Tornado className="h-2.5 w-2.5 mr-0.5" /> tornado reported
                    </Badge>
                )}
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded((v) => !v)}
                    className="ml-auto h-6 text-xs text-amber-800 hover:bg-amber-100 px-2"
                    data-testid="zip-storm-expand"
                >
                    {expanded ? 'Hide details' : 'See breakdown'} <ChevronRight className={`h-3 w-3 ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </Button>
            </div>
            {expanded && data && (
                <div className="mt-2">
                    <StormIntelCard
                        lat={data.lat}
                        lng={data.lng}
                        state={data.state}
                        radiusMiles={radiusMiles}
                        daysBack={daysBack}
                        title={`Storm activity in ZIP ${zip}`}
                    />
                </div>
            )}
        </div>
    );
}
